import React, { useEffect, useState } from 'react';
import { AdminStats } from '../../types';
import { fetchAdminStats, syncAllExamsToSupabase } from '../../services/dataService';

interface AdminOverviewProps {
    categoriesCount: number;
    onNavigateToTab: (tab: string) => void;
}

const AdminOverview: React.FC<AdminOverviewProps> = ({ categoriesCount, onNavigateToTab }) => {
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<{ success: number, failed: number } | null>(null);

    const handleSyncToCloud = async () => {
        setSyncing(true);
        setSyncResult(null);
        try {
            const result = await syncAllExamsToSupabase();
            setSyncResult({ success: result.success, failed: result.failed });
            if (result.failed > 0) {
                alert(`Sync completed with errors:\n${result.errors.join('\n')}`);
            } else {
                alert(`Successfully synced ${result.success} exams to cloud!`);
            }
        } catch (err: any) {
            alert('Sync failed: ' + err.message);
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        const loadStats = async () => {
            const data = await fetchAdminStats();
            setStats(data);
        };
        loadStats();
    }, []);

    return (
        <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Total Users</p>
                            <h3 className="text-3xl font-bold text-slate-800 dark:text-white mt-1">{stats?.totalUsers ?? '...'}</h3>
                        </div>
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/30 text-blue-600 rounded-lg">
                            <i className="fas fa-users text-xl"></i>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center text-sm text-green-600 font-medium">
                        <i className="fas fa-arrow-up mr-1"></i> Growing
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Total Sets</p>
                            <h3 className="text-3xl font-bold text-slate-800 dark:text-white mt-1">{stats?.totalExams ?? '...'}</h3>
                        </div>
                        <div className="p-3 bg-purple-50 dark:bg-purple-900/30 text-purple-600 rounded-lg">
                            <i className="fas fa-file-alt text-xl"></i>
                        </div>
                    </div>
                    <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                        Across {categoriesCount} categories
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Question Bank</p>
                            <h3 className="text-3xl font-bold text-slate-800 dark:text-white mt-1">{stats?.totalQuestions ?? '...'}</h3>
                        </div>
                        <div className="p-3 bg-orange-50 dark:bg-orange-900/30 text-orange-600 rounded-lg">
                            <i className="fas fa-database text-xl"></i>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center text-sm text-green-600 font-medium">
                        <i className="fas fa-check mr-1"></i> Verified
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">Total Attempts</p>
                            <h3 className="text-3xl font-bold text-slate-800 dark:text-white mt-1">{stats?.totalAttempts ?? '...'}</h3>
                        </div>
                        <div className="p-3 bg-green-50 dark:bg-green-900/30 text-green-600 rounded-lg">
                            <i className="fas fa-pen-alt text-xl"></i>
                        </div>
                    </div>
                    <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                        All time
                    </div>
                </div>
            </div>

            {/* Sync to Cloud Section */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 rounded-lg shadow-sm mb-8">
                <div className="flex items-center justify-between">
                    <div className="text-white">
                        <h3 className="text-lg font-bold flex items-center">
                            <i className="fas fa-cloud-upload-alt mr-2"></i>
                            Sync Data to Cloud
                        </h3>
                        <p className="text-blue-100 text-sm mt-1">
                            Push all your local exams to Supabase so other users can see them.
                            {syncResult && (
                                <span className="ml-2 bg-white/20 px-2 py-0.5 rounded">
                                    Last sync: {syncResult.success} ✓ {syncResult.failed > 0 ? `, ${syncResult.failed} ✗` : ''}
                                </span>
                            )}
                        </p>
                    </div>
                    <button
                        onClick={handleSyncToCloud}
                        disabled={syncing}
                        className="px-6 py-3 bg-white text-purple-600 rounded-lg font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center"
                    >
                        {syncing ? (
                            <><i className="fas fa-spinner fa-spin mr-2"></i> Syncing...</>
                        ) : (
                            <><i className="fas fa-sync mr-2"></i> Sync Now</>
                        )}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6">User Distribution</h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="w-3 h-3 rounded-full bg-slate-400 mr-3"></div>
                                <span className="text-slate-600 dark:text-slate-300 font-medium">Free Users</span>
                            </div>
                            <span className="font-bold text-slate-800 dark:text-white">{stats?.usersByTier.free ?? '-'}</span>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2">
                            <div
                                className="bg-slate-400 h-2 rounded-full"
                                style={{ width: `${stats ? (stats.usersByTier.free / (stats.totalUsers || 1)) * 100 : 0}%` }}
                            ></div>
                        </div>

                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center">
                                <div className="w-3 h-3 rounded-full bg-orange-500 mr-3"></div>
                                <span className="text-slate-600 dark:text-slate-300 font-medium">Pro Users</span>
                            </div>
                            <span className="font-bold text-slate-800 dark:text-white">{stats?.usersByTier.pro ?? '-'}</span>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2">
                            <div
                                className="bg-orange-500 h-2 rounded-full"
                                style={{ width: `${stats ? (stats.usersByTier.pro / (stats.totalUsers || 1)) * 100 : 0}%` }}
                            ></div>
                        </div>

                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center">
                                <div className="w-3 h-3 rounded-full bg-purple-600 mr-3"></div>
                                <span className="text-slate-600 dark:text-slate-300 font-medium">Premium Users</span>
                            </div>
                            <span className="font-bold text-slate-800 dark:text-white">{stats?.usersByTier.premium ?? '-'}</span>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2">
                            <div
                                className="bg-purple-600 h-2 rounded-full"
                                style={{ width: `${stats ? (stats.usersByTier.premium / (stats.totalUsers || 1)) * 100 : 0}%` }}
                            ></div>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                    <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6">Quick Actions</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <button
                            onClick={() => onNavigateToTab('csv')}
                            className="p-4 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all text-center group"
                        >
                            <i className="fas fa-file-csv text-2xl text-slate-400 group-hover:text-orange-500 mb-2"></i>
                            <div className="font-medium text-slate-700 dark:text-slate-300">Upload CSV</div>
                        </button>
                        <button
                            onClick={() => onNavigateToTab('ai')}
                            className="p-4 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all text-center group"
                        >
                            <i className="fas fa-robot text-2xl text-slate-400 group-hover:text-purple-500 mb-2"></i>
                            <div className="font-medium text-slate-700 dark:text-slate-300">AI Generator</div>
                        </button>
                        <button
                            onClick={() => onNavigateToTab('papers')}
                            className="p-4 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-center group"
                        >
                            <i className="fas fa-plus-circle text-2xl text-slate-400 group-hover:text-blue-500 mb-2"></i>
                            <div className="font-medium text-slate-700 dark:text-slate-300">New Paper</div>
                        </button>
                        <button
                            onClick={() => onNavigateToTab('reports')}
                            className="p-4 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all text-center group"
                        >
                            <i className="fas fa-flag text-2xl text-slate-400 group-hover:text-red-500 mb-2"></i>
                            <div className="font-medium text-slate-700 dark:text-slate-300">View Reports</div>
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdminOverview;
